import { ClassRecord } from '../data/classes'

// Arizona does not observe daylight saving time -> America/Phoenix is UTC-7 year round.
const AZ_TZID = 'America/Phoenix'
const AZ_OFFSET_MINUTES = -7 * 60

interface ParsedTime {
  hours: number // 0-23
  minutes: number
}

/** Parse a display time like "9:00 AM" or "1:00 PM" into 24h hours/minutes. */
function parseTime(time: string): ParsedTime | null {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  const meridiem = match[3].toUpperCase()
  if (meridiem === 'PM' && hours !== 12) hours += 12
  if (meridiem === 'AM' && hours === 12) hours = 0
  if (hours > 23 || minutes > 59) return null
  return { hours, minutes }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "20260622T090000" — local (floating, paired with TZID). */
function formatLocal(dateStr: string, t: ParsedTime): string {
  const [y, m, d] = dateStr.split('-')
  return `${y}${m}${d}T${pad(t.hours)}${pad(t.minutes)}00`
}

/** "20260622T160000Z" — UTC equivalent of a Phoenix wall-clock time. */
function formatUTC(dateStr: string, t: ParsedTime): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  // Phoenix is UTC-7; UTC = local - offset.
  const utc = new Date(
    Date.UTC(y, m - 1, d, t.hours, t.minutes) - AZ_OFFSET_MINUTES * 60000,
  )
  return (
    `${utc.getUTCFullYear()}${pad(utc.getUTCMonth() + 1)}${pad(utc.getUTCDate())}` +
    `T${pad(utc.getUTCHours())}${pad(utc.getUTCMinutes())}00Z`
  )
}

/** "2026-06-22T09:00:00-07:00" — ISO with Arizona offset (for web calendar links). */
function formatISOOffset(dateStr: string, t: ParsedTime): string {
  return `${dateStr}T${pad(t.hours)}:${pad(t.minutes)}:00-07:00`
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/['’"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

/** Escape text for an ICS field per RFC 5545. */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

/** Human-readable, multi-detail description shared by all calendar formats. */
function buildDescription(cls: ClassRecord, registerUrl?: string): string {
  const lines = [
    cls.title,
    '',
    `CE Category: ${cls.ceCategory}`,
    `Hours: ${cls.hours}`,
    `Format: ${cls.format}`,
    `Instructor: ${cls.instructorName} (${cls.instructorGroup})`,
    `Price: ${cls.price}`,
    cls.format === 'Zoom'
      ? `Zoom: ${cls.location}`
      : `Location: ${cls.location}`,
  ]
  if (registerUrl && registerUrl !== '#') {
    lines.push('', `Register / Details: ${registerUrl}`)
  } else {
    lines.push(
      '',
      'To register, contact Desert Sage Seminars at 602-377-6207 or via the Contact page.',
    )
  }
  return lines.join('\n')
}

export interface CalendarResult {
  ok: boolean
  message?: string
}

/**
 * Build a full .ics document for a single class, in America/Phoenix time.
 * Returns null if the class is missing the data required to build an event.
 */
export function buildICS(
  cls: ClassRecord,
  registerUrl?: string,
): string | null {
  if (!cls.date) return null
  const start = parseTime(cls.startTime)
  const end = parseTime(cls.endTime)
  if (!start || !end) return null

  const now = new Date()
  const dtstamp =
    `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}` +
    `T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`

  const location =
    cls.format === 'Zoom' ? `Zoom — ${cls.location}` : cls.location

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Desert Sage Seminars//Class Schedule//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    // Arizona time zone definition (no DST)
    'BEGIN:VTIMEZONE',
    `TZID:${AZ_TZID}`,
    'BEGIN:STANDARD',
    'DTSTART:19700101T000000',
    'TZOFFSETFROM:-0700',
    'TZOFFSETTO:-0700',
    'TZNAME:MST',
    'END:STANDARD',
    'END:VTIMEZONE',
    'BEGIN:VEVENT',
    `UID:desert-sage-${cls.id}-${cls.date}@desertsageseminars.com`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=${AZ_TZID}:${formatLocal(cls.date, start)}`,
    `DTEND;TZID=${AZ_TZID}:${formatLocal(cls.date, end)}`,
    `SUMMARY:${escapeICS(cls.title)}`,
    `LOCATION:${escapeICS(location)}`,
    `DESCRIPTION:${escapeICS(buildDescription(cls, registerUrl))}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]

  // RFC 5545 requires CRLF line endings.
  return lines.join('\r\n')
}

export function icsFilename(cls: ClassRecord): string {
  return `desert-sage-${slugify(cls.title)}-${cls.date}.ics`
}

/** Generate and download the .ics file for the given class. */
export function downloadICS(
  cls: ClassRecord,
  registerUrl?: string,
): CalendarResult {
  const ics = buildICS(cls, registerUrl)
  if (!ics) {
    return {
      ok: false,
      message: 'Calendar details are unavailable for this class.',
    }
  }
  try {
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = icsFilename(cls)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Defer revoke so the download can start.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return { ok: true }
  } catch {
    return { ok: false, message: 'Could not generate the calendar file.' }
  }
}

/** Build a Google Calendar "add event" URL (opens in a new tab). */
export function googleCalendarUrl(
  cls: ClassRecord,
  registerUrl?: string,
): string | null {
  if (!cls.date) return null
  const start = parseTime(cls.startTime)
  const end = parseTime(cls.endTime)
  if (!start || !end) return null

  const location =
    cls.format === 'Zoom' ? `Zoom — ${cls.location}` : cls.location

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: cls.title,
    dates: `${formatUTC(cls.date, start)}/${formatUTC(cls.date, end)}`,
    details: buildDescription(cls, registerUrl),
    location,
    ctz: AZ_TZID,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Build an Outlook web "compose event" URL (opens in a new tab). */
export function outlookCalendarUrl(
  cls: ClassRecord,
  registerUrl?: string,
): string | null {
  if (!cls.date) return null
  const start = parseTime(cls.startTime)
  const end = parseTime(cls.endTime)
  if (!start || !end) return null

  const location =
    cls.format === 'Zoom' ? `Zoom — ${cls.location}` : cls.location

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: cls.title,
    startdt: formatISOOffset(cls.date, start),
    enddt: formatISOOffset(cls.date, end),
    body: buildDescription(cls, registerUrl),
    location,
  })
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
}
